import React, { useState } from 'react';
import { Button, Form, Input, Message, Modal, SelectPicker } from 'rsuite';
import { uploadBagelDocument } from '../../../api/bagel-documents';
import { BagelDocument, BagelDocumentCategory } from '../../../types';
import AutoGrowTextarea from '../AutoGrowTextarea';
import { CATEGORY_OPTIONS } from '../../constants/bagelDocumentFormOptions';

export interface BagelDocumentUploadModalProps {
  onClose: () => void;
  onUploaded: (doc: BagelDocument) => void;
}

export default function BagelDocumentUploadModal({
  onClose,
  onUploaded,
}: BagelDocumentUploadModalProps) {
  const [name, setName] = useState('');
  const [category, setCategory] =
    useState<BagelDocumentCategory>('urban_txt');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    if (selected && !name.trim()) {
      const base = selected.name.replace(/\.[^.]+$/, '');
      setName(base);
    }
  };

  const handleSubmit = async () => {
    if (!file || !name.trim()) return;
    setUploading(true);
    setError(null);
    try {
      const doc = await uploadBagelDocument({
        file,
        name: name.trim(),
        category,
        description: description.trim() || undefined,
      });
      onUploaded(doc);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal open onClose={onClose} size="sm">
      <Modal.Header>
        <Modal.Title>Upload Document</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Message type="error" showIcon style={{ marginBottom: 12 }}>
            {error}
          </Message>
        )}
        <Form fluid>
          <Form.Group>
            <Form.ControlLabel>Document Name *</Form.ControlLabel>
            <Input
              value={name}
              onChange={v => setName(String(v))}
              placeholder="SMS Compliance Guidelines"
            />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Category *</Form.ControlLabel>
            <SelectPicker
              data={CATEGORY_OPTIONS}
              value={category}
              onChange={v =>
                setCategory((v as BagelDocumentCategory) || 'guidelines')
              }
              cleanable={false}
              block
            />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Description</Form.ControlLabel>
            <AutoGrowTextarea
              value={description}
              onChange={v => setDescription(String(v))}
              placeholder="Optional short description"
              minRows={2}
            />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>
              File * (PDF, DOCX, TXT, MD — max 20 MB)
            </Form.ControlLabel>
            <input
              type="file"
              accept=".pdf,.docx,.txt,.md"
              onChange={handleFileChange}
              style={{ display: 'block', marginTop: 4 }}
            />
            {file ? (
              <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
                Source file: <strong>{file.name}</strong>
              </div>
            ) : null}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} appearance="subtle">
          Cancel
        </Button>
        <Button
          appearance="primary"
          onClick={handleSubmit}
          disabled={!file || !name.trim() || uploading}
          loading={uploading}
        >
          Upload
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
